import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Dropdown, DropdownWithProperties } from '../shared/models/dropdown';
import {
  Criteria,
  GenerateReportResult,
  NextDateResult,
  RecipientTankCounts,
  RecipientTankCountsRequest,
  ReportSettingsSaveDto,
  ReportUserDto,
} from '../interfaces/reportSettingsSaveDto';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { api, part } from '../shared/communication-objects/api-path';
import { HttpParams } from '@angular/common/http';
import { IFilterProfile, ITankProfile } from '../interfaces/tankProfile';
import { IReportScope } from '../interfaces/reportScope';
import { ReportRunDto } from '../interfaces/reportRunDto';
import { ReportRunStatusDto } from '../interfaces/reportRunStatusDto';
import { ReportRunRecipientDto } from '../interfaces/reportRunRecipientDto';
import { ReportScheduleOverview } from '../interfaces/reportScheduleStatus';

@Injectable({
  providedIn: 'root',
})
export class ReportSettingsService {

  /** Tank Count and each listed recipient's tank count, for the report as the form holds it. */
  getRecipientTankCounts(request: RecipientTankCountsRequest): Observable<RecipientTankCounts> {
    return this.restClient.postForResponse<RecipientTankCounts>(api`${this.apiUrl}/recipient-tank-counts`, request);
  }

  private readonly apiUrl = api`ReportSettings`;

  constructor(private restClient: DSRESTClient) {}

  saveReportSettings(
    reportSettings: ReportSettingsSaveDto,
  ): Observable<number> {
    return this.restClient.postForResponse<number>(
      api`${this.apiUrl}`,
      reportSettings,
    );
  }

  saveReportSettingsAndGenerate(
    reportSettings: ReportSettingsSaveDto,
  ): Observable<GenerateReportResult> {
    return this.restClient.postForResponse<GenerateReportResult>(
      api`${this.apiUrl}/generate`,
      reportSettings,
    );
  }

  /** Generate Now for the saved report as it stands, without saving its settings - the Generate Now screen's "Try again". */
  generateNow(reportId: number): Observable<GenerateReportResult> {
    return this.restClient.postForResponse<GenerateReportResult>(api`${this.apiUrl}/reports/${reportId}/generate`, {});
  }

  /** Whether the report will go out on schedule, with its schedule line - the same as its Reports grid row. */
  getScheduleOverview(reportId: number): Observable<ReportScheduleOverview> {
    return this.restClient.getAsModel<ReportScheduleOverview>(api`${this.apiUrl}/reports/${reportId}/status`);
  }

  /**
   * Moves a report that is behind to its next future run; the runs it missed are not sent. Takes the date the user saw
   * it due since (status.dueSince); the server answers 409 if it has moved since.
   */
  skipMissedRuns(reportId: number, expectedNextReportDate: string): Observable<ReportScheduleOverview> {
    return this.restClient.postForResponse<ReportScheduleOverview>(api`${this.apiUrl}/reports/${reportId}/skip-missed`, {
      expectedNextReportDate,
    });
  }

  /** Status of the batch Generate Now started, or of the report's current batch when there is none. */
  getStatus(reportId: number, batchId?: string | null): Observable<ReportRunStatusDto> {
    return this.restClient.getAsModel<ReportRunStatusDto>(
      batchId
        ? api`${this.apiUrl}/status/${reportId}?batchId=${part(batchId)}`
        : api`${this.apiUrl}/status/${reportId}`,
    );
  }

  /** Most recent runs of a report schedule - Generate Now and TapReporting's own recurring runs alike - newest first. */
  getRunHistory(reportId: number, take = 20): Observable<ReportRunDto[]> {
    return this.restClient.getAsModel<ReportRunDto[]>(
      api`${this.apiUrl}/reports/${reportId}/runs?take=${take}`,
    );
  }

  /** Per-recipient delivery breakdown for one batch, for the Generate Now screen's expandable detail. */
  getRunRecipients(reportId: number, batchId: string): Observable<ReportRunRecipientDto[]> {
    return this.restClient.getAsModel<ReportRunRecipientDto[]>(
      api`${this.apiUrl}/reports/${reportId}/runs/${part(batchId)}/recipients`,
    );
  }

  getReportTypes(): Observable<Dropdown<number>[]> {
    return this.restClient.getAsModel<Dropdown<number>[]>(
      api`${this.apiUrl}/report-types`,
    );
  }

  getTimezones(): Observable<DropdownWithProperties<number, { adjustment: number }>[]> {
    return this.restClient.getAsModel<DropdownWithProperties<number, { adjustment: number }>[]>(
      api`${this.apiUrl}/timezones`,
    );
  }

  getDateRangeOptions(id: number | null): Observable<Dropdown<number>[]> {
    const newLocal = id === null;
    const path = newLocal
      ? api`${this.apiUrl}/date-range-options`
      : api`${this.apiUrl}/date-range-options/${id}`;
    return this.restClient.getAsModel<Dropdown<number>[]>(path);
  }

  loadTankReportSettings(id: number): Observable<ReportSettingsSaveDto> {
    return this.restClient.getAsModel<ReportSettingsSaveDto>(
      api`${this.apiUrl}/reports/${id}`,
    );
  }

  /**
   * Owning organization and CSO-branch answer for one report, for ReportNavigationGuard. Kept off
   * loadTankReportSettings so the guard does not pull the whole report graph - recipients, their
   * emails, their users and those users' organizations - on every navigation.
   */
  getReportScope(id: number): Observable<IReportScope> {
    return this.restClient.getAsModel<IReportScope>(
      api`${this.apiUrl}/reports/${id}/scope`,
    );
  }

  getReportNames(reportId: number | null = null): Observable<string[]> {
    const reportNamesPath = reportId
      ? api`${this.apiUrl}/report-names/${reportId}`
      : api`${this.apiUrl}/report-names`;
    return this.restClient.getAsModel<string[]>(reportNamesPath);
  }

  getUnitOfMeasures(): Observable<Dropdown<number>[]> {
    return this.restClient.getAsModel<Dropdown<number>[]>(
      api`${this.apiUrl}/unit-of-measures`);
  }

  getNextRunTime(request: Criteria): Observable<NextDateResult> {
    let httpParams = new HttpParams();
    return this.restClient.postForResponse<NextDateResult>(
      api`${this.apiUrl}/generate-next-run-time?${httpParams}`,
      request,
    );
  }

  getTankProfiles(reportId: number | null, mode : string | null): Observable<ITankProfile[]> {
    if (reportId) {
      return this.restClient.getAsModel<ITankProfile[]>(
        api`${this.apiUrl}/get-tank-profiles/${reportId}/${part(mode)}`
      );
    }
    else
    {
      return this.restClient.getAsModel<ITankProfile[]>(
        api`${this.apiUrl}/get-tank-profiles`
      );
    }
  }

  getFilterProfiles(reportId: number | null, mode: string | null): Observable<any> {
    if (reportId) {
      return this.restClient.getAsModel<IFilterProfile[]>(
        api`${this.apiUrl}/get-filter-profiles/${reportId}/${part(mode)}`
      );
    }
    else
    {
      return this.restClient.getAsModel<IFilterProfile[]>(
        api`${this.apiUrl}/get-filter-profiles`
      );
    }
  }

  getReportStartDate(timezoneId: number | null): Observable<NextDateResult> {
    let httpParams = new HttpParams();
    return this.restClient.postForResponse<NextDateResult>(
      api`${this.apiUrl}/get-report-start-date?${httpParams}`,
      timezoneId,
    );
  }


  getUsers(organizationId: number) : Observable<ReportUserDto[]> {
    return this.restClient.getAsModel<ReportUserDto[]>(
      api`${this.apiUrl}/get-users/${organizationId}`);
  }


  // getTimesNoEarlier(): Observable<Dropdown<number>[]> {
  //   return this.http.get<Dropdown<number>[]>(
  //     `${this.apiUrl}/GetTimesNoEarlier`
  //   );
  // }

  // getDateRange(request: GetDateRangeRequestDto): Observable<GetDateRangeResponseDto> {
  //   return this.http.post<GetDateRangeResponseDto>(`${this.apiUrl}/GetDateRange`, request);
  // }
}
